import { useMemo, useState, useEffect, useRef } from "react";
import { Button, Chip, Icon, SearchBar, Select, Toast } from "@legal-500/legal500-design-system";
import type { Column } from "@legal-500/legal500-design-system";
import type { ToastType } from "@legal-500/legal500-design-system";
import { randomLibrary } from "@/api/random-library.service";
import type {
  CreateQuestionBody,
  LibraryQuestion,
  UpdateQuestionBody,
} from "@/api/survey-library.service";
import { getLocalizedTextDefault } from "@/api/survey-library.service";
import { ErrorBanner } from "@/components/ErrorBanner/ErrorBanner";
import EmptyState from "@/components/EmptyState";
import LoadingState from "@/components/LoadingState";
import { ConfirmationModal } from "@/components/Modal/ConfirmationModal";
import { PageHeader } from "@/components/PageHeader";
import { DataTable } from "@/components/DataTable/DataTable";
import { PaginationControls } from "@/components/PaginationControls";
import { useMutation, useQuery } from "@/hooks/useApi";
import { useSupportedLanguages } from "@/hooks/useSupportedLanguages";
import { debounce } from "@/utils";
import { QuestionFormModal } from "../QuestionLibrary/QuestionFormModal";
import { SURVEY_QUESTION_TYPE_CONFIG } from "../questionTypes";
import styles from "./RandomQuestions.module.css";

const DEFAULT_ITEMS_PER_PAGE = 10;

function RandomQuestionsPage() {
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(DEFAULT_ITEMS_PER_PAGE);
  const [editingQuestion, setEditingQuestion] = useState<LibraryQuestion | null>(null);
  const [isQuestionModalOpen, setIsQuestionModalOpen] = useState(false);
  const [deletingQuestion, setDeletingQuestion] = useState<LibraryQuestion | null>(null);
  const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null);
  const applyDebouncedSearchRef = useRef<(value: string) => void>((_value: string) => undefined);
  applyDebouncedSearchRef.current = (value: string) => {
    setDebouncedSearchTerm(value);
    setCurrentPage(prev => (prev === 1 ? prev : 1));
  };
  const debouncedSearchChangeRef = useRef(
    debounce((value: unknown) => {
      applyDebouncedSearchRef.current(String(value));
    }, 300),
  );
  const debouncedSearchChange = debouncedSearchChangeRef.current;

  useEffect(() => {
    return () => debouncedSearchChangeRef.current.cancel();
  }, []);

  const offset = (currentPage - 1) * itemsPerPage;

  const {
    data: libraryResponse,
    loading,
    error,
    refetch,
    isRefetching,
  } = useQuery(
    () =>
      randomLibrary.list({
        search: debouncedSearchTerm || undefined,
        limit: itemsPerPage,
        offset,
      }),
    [debouncedSearchTerm, currentPage, itemsPerPage],
  );
  const { translatableLanguages } = useSupportedLanguages();
  const { mutate: createQuestion } = useMutation((data: CreateQuestionBody) =>
    randomLibrary.create(data),
  );
  const { mutate: updateQuestion } = useMutation((args: { id: string; data: UpdateQuestionBody }) =>
    randomLibrary.update(args.id, args.data),
  );
  const { mutate: deleteQuestion } = useMutation((id: string) => randomLibrary.remove(id));

  const randomQuestions = useMemo(
    () => libraryResponse?.questions ?? [],
    [libraryResponse?.questions],
  );
  const totalCount = libraryResponse?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / itemsPerPage));

  const handleAddClick = () => {
    setEditingQuestion(null);
    setIsQuestionModalOpen(true);
  };

  const handleEditClick = (question: LibraryQuestion) => {
    setEditingQuestion(question);
    setIsQuestionModalOpen(true);
  };

  const handleSaveQuestion = async (data: CreateQuestionBody | UpdateQuestionBody, id?: string) => {
    if (id) {
      await updateQuestion({ id, data });
      setToast({ message: "Random question updated.", type: "success" });
    } else {
      await createQuestion(data);
      setToast({ message: "Random question added.", type: "success" });
    }
    refetch();
  };

  const handleDeleteConfirm = async () => {
    if (!deletingQuestion) return;
    try {
      await deleteQuestion(deletingQuestion.id);
      setDeletingQuestion(null);
      setToast({ message: "Random question deleted.", type: "success" });
      refetch();
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to delete question.";
      setToast({ message, type: "error" });
    }
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    debouncedSearchChange(value);
  };

  const handleSearchClear = () => {
    debouncedSearchChange.cancel();
    setSearchTerm("");
    setDebouncedSearchTerm("");
    setCurrentPage(1);
  };

  const columns: Column<LibraryQuestion>[] = [
    {
      header: "Question",
      accessor: "text" as keyof LibraryQuestion,
      render: (_value: unknown, row: LibraryQuestion) => (
        <div className={styles.questionCell}>
          <span className={styles.questionText}>{getLocalizedTextDefault(row.text)}</span>
          {getLocalizedTextDefault(row.description).trim() !== "" && (
            <span className={styles.questionDescription}>
              {getLocalizedTextDefault(row.description).trim()}
            </span>
          )}
          {row.required && (
            <div className={styles.questionMeta}>
              <Chip size="small" variant="outlined" color="error">
                Required
              </Chip>
            </div>
          )}
        </div>
      ),
    },
    {
      header: "Type",
      accessor: "type" as keyof LibraryQuestion,
      render: (_value: unknown, row: LibraryQuestion) => (
        <Chip size="small" variant="outlined" color="primary">
          {SURVEY_QUESTION_TYPE_CONFIG[row.type]?.label ?? row.type}
        </Chip>
      ),
    },
    {
      header: "Actions",
      accessor: "createdAt" as keyof LibraryQuestion,
      render: (_value: unknown, row: LibraryQuestion) => (
        <div className={styles.actionsCell}>
          <Button
            variant="outlined"
            size="small"
            aria-label="Edit question and translations"
            onClick={() => handleEditClick(row)}
          >
            <Icon icon="edit" size="small" />
          </Button>
          <Button
            variant="outlined"
            color="error"
            size="small"
            aria-label="Delete question"
            onClick={() => setDeletingQuestion(row)}
          >
            <Icon icon="delete" size="small" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className={styles.wrapper}>
      <PageHeader
        title="Random Questions"
        subtitle="Global random-question bucket used across surveys. All target-language translations are required before save."
      >
        <Button
          variant="contained"
          color="primary"
          onClick={handleAddClick}
          startIcon={<Icon icon="plus" size="small" />}
        >
          Add Random Question
        </Button>
      </PageHeader>

      {error && <ErrorBanner error={error.message} onRetry={refetch} loading={isRefetching} />}

      <div className={styles.controlsRow}>
        <div className={styles.searchBar}>
          <SearchBar
            value={searchTerm}
            onChange={handleSearchChange}
            onClear={handleSearchClear}
            placeholder="Search random questions..."
          />
        </div>
        <div className={styles.rowsPerPageControl}>
          <label htmlFor="random-items-per-page" className={styles.rowsPerPageLabel}>
            Rows per page:
          </label>
          <div className={styles.rowsPerPageSelect}>
            <Select
              id="random-items-per-page"
              value={itemsPerPage}
              onChange={event => {
                setItemsPerPage(Number(event.target.value));
                setCurrentPage(1);
              }}
              options={[
                { value: 10, label: "10" },
                { value: 25, label: "25" },
                { value: 50, label: "50" },
                { value: 100, label: "100" },
              ]}
              size="small"
            />
          </div>
        </div>
      </div>

      {loading && libraryResponse === null ? (
        <LoadingState message="Loading random questions..." />
      ) : !error && randomQuestions.length === 0 ? (
        <EmptyState
          variant="empty"
          title="No random questions available"
          message="Questions marked as random will appear here."
        />
      ) : (
        !error && (
          <>
            <div className={styles.tableContainer}>
              <DataTable
                className={styles.randomQuestionsDataTable}
                data={randomQuestions}
                columns={columns}
                emptyMessage="No random questions available."
              />
            </div>
            <PaginationControls
              currentPage={currentPage}
              totalPages={totalPages}
              startIndex={offset}
              endIndex={offset + randomQuestions.length}
              totalCount={totalCount}
              itemName="questions"
              onPageChange={setCurrentPage}
            />
          </>
        )
      )}

      <QuestionFormModal
        isOpen={isQuestionModalOpen}
        onClose={() => {
          setIsQuestionModalOpen(false);
          setEditingQuestion(null);
        }}
        question={editingQuestion}
        onSave={handleSaveQuestion}
        onSaveError={message => setToast({ message, type: "error" })}
        showDisplayRules={false}
        translatableLanguages={translatableLanguages}
        requireTranslations
        useCreateTwoStepFlow={true}
        autoTranslateQuestionText={false}
        showDescriptionTranslationFields={false}
      />

      {deletingQuestion && (
        <ConfirmationModal
          size="small"
          title="Delete Random Question"
          message="Are you sure you want to delete this random question?"
          warningMessage="This action cannot be undone."
          backdropClick={false}
          affectedItems={[
            { id: deletingQuestion.id, name: getLocalizedTextDefault(deletingQuestion.text) },
          ]}
          confirmButtonText="Delete"
          confirmButtonColor="error"
          onClose={() => setDeletingQuestion(null)}
          onConfirm={handleDeleteConfirm}
        />
      )}

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          duration={toast.type === "error" ? 5000 : 3000}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
}

export default RandomQuestionsPage;
